import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { FormGroup, FormLabel, FormInput } from '../../components/ui/form';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useToast } from '../../contexts/ToastContext';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const addToast = useToast();

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        setError(error.message);
        addToast(error.message, 'danger');
      } else {
        setSuccess('Check your email for the magic link to log in.');
        addToast('Magic link sent! Check your inbox.', 'success');
      }
    } catch (error) {
      console.error('Error sending magic link:', error);
      setError('Failed to send magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) {
        setError(error.message);
        addToast(error.message, 'danger');
      } else {
        setSuccess('Check your email for the password reset link.');
        addToast('Password reset link sent! Check your inbox.', 'success');
      }
    } catch (error) {
      console.error('Error sending password reset:', error);
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">Account Recovery</CardTitle>
          <p className="text-center text-gray-600 dark:text-gray-400">
            Enter your email address and choose how you'd like to recover your account.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <FormGroup>
              <FormLabel htmlFor="email">Email address</FormLabel>
              <FormInput
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </FormGroup>
            
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
              </div>
            )}
            
            <div className="space-y-3">
              <button
                onClick={handleMagicLink}
                disabled={loading || !email}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="fas fa-magic me-2"></i>Send Magic Link
                  </>
                )}
              </button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400">or</span>
                </div>
              </div>
              
              <button
                onClick={handlePasswordReset}
                disabled={loading || !email}
                className="w-full py-2 px-4 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="fas fa-key me-2"></i>Reset My Password
                  </>
                )}
              </button>
            </div>
            
            <div className="text-center mt-4">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                <strong>Magic Link:</strong> Get logged in instantly (no password needed)
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                <strong>Password Reset:</strong> Set a new password for your account
              </div>
            </div>
          </div>
          
          <div className="flex justify-center mt-6">
            <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
              <i className="fas fa-arrow-left me-2"></i>Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ForgotPassword;